(() => {
  const config = JSON.parse(document.getElementById('keysrus-search-settings')?.textContent || '{}');
  const copy = config.copy || {};
  const normalize = value => String(value).normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const quote = value => `"${String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
  const contains = (text, words) => (` ${text} `).includes(` ${words} `);
  // Vehicle makes are also OEM vendors in the catalog. They describe fitment, never a brand
  // restriction: "toyota smart key" must also find aftermarket Toyota keys.
  const vehicleMakes = /^(?:acura|alfa romeo|audi|bentley|bmw|buick|cadillac|chevrolet|chevy|chrysler|dodge|fiat|ford|freightliner|genesis|gm|gmc|honda|hummer|hyundai|infiniti|international|isuzu|jaguar|jeep|kenworth|kia|land rover|lexus|lincoln|mack|maserati|mazda|mercedes|mercedes benz|mini|mitsubishi|nissan|peterbilt|pontiac|porsche|ram|range rover|rolls royce|saab|saturn|scion|subaru|suzuki|tesla|toyota|volkswagen|vw|volvo)$/;
  const brands = (config.vendors || []).filter(Boolean).map(name => ({ name, words: normalize(name) === 'hid global' ? 'hid' : normalize(name) })).filter(brand => brand.words && !vehicleMakes.test(brand.words)).sort((a, b) => b.words.length - a.words.length);
  const anyTitle = values => `(${values.map(value => `title:${quote(value)}`).join(' OR ')})`;
  const exclusions = values => values.map(value => `-title:${quote(value)}`).join(' ');
  const rules = [
    { key: 'cutting', match: /\b(?:key cutting(?: machines?)?|key cutters?|key duplicators?|machines? (?:for )?key cutting)\b/, titles: ['key cutting machine', 'key cutter', 'key duplicator'], exclude: ['cutter bit', 'milling bit', 'tracer', 'replacement blade', 'replacement cutter', 'cutting wheel', 'jaw', 'clamp', 'cover', 'belt', 'brush', 'probe', 'adapter', 'blank', 'chip', 'cloning', 'transponder', 'gauge', 'blade'] },
    { key: 'programming', match: /\b(?:key programmers?|key programming(?: tools?|machines?|devices?)?)\b/, titles: ['key programmer', 'key programming', 'key tool'], exclude: ['button', 'flip', 'wired', 'proximity', 'shell', 'blade', 'adapter', 'cable', 'clip', 'protective case'] },
    { key: 'remote', match: /\b(?:car remotes?|smart keys?|smart remotes?)\b/, titles: ['smart key', 'remote', 'fobik'], exclude: ['programmer', 'programming', 'cloner', 'reader', 'pcb', 'board', 'shell', 'case', 'cable'] },
    { key: 'chip', match: /\bsuper chips?\b/, titles: ['super chip'], exclude: ['remote', 'flip', 'smart key'] }
  ];

  // Queries whose interpreted search found nothing on this page stay native for filters and pagination.
  const nativeQueries = new Set();
  const preferNative = value => nativeQueries.add(String(value || '').trim());

  function plan(value, raw = false) {
    const original = String(value || '').trim();
    let words = normalize(original);
    const result = { original, query: original, label: original, kind: 'native', refinements: [] };
    if (!original || raw || nativeQueries.has(original) || original.length > 180 || /(?:\w+:|["()])/.test(original)) return result;
    if (/\b(?:19|20)\d{2}\b/.test(words) && /\b(?:car|vehicle|keys?|remotes?|honda|toyota|nissan|ford|chevrolet|civic|accord|mazda|hyundai|kia|subaru|lexus|acura|bmw|audi|volkswagen|mercedes|dodge|jeep|chrysler|gmc|buick|cadillac|mitsubishi|volvo|tesla)\b/.test(words) && !/\b(?:programmers?|programming|cutting|chips?|readers?|credentials?|mircom|hid)\b/.test(words)) {
      return result;
    }
    if (/^xo super chips?$/.test(words) && brands.some(brand => brand.words === 'xhorse')) {
      words = words.replace(/^xo /, 'xhorse ');
      result.label = 'Xhorse super chip';
      result.corrected = true;
    }
    if (/^(?=.*[a-z])(?=.*\d)[a-z0-9-]{3,40}$/i.test(original)) {
      result.kind = 'catalog';
      result.query = `(title:${quote(original)} OR variants.sku:${quote(original)} OR variants.barcode:${quote(original)})`;
      return result;
    }
    const matches = brands.filter(brand => contains(words, brand.words));
    const brand = matches.find(candidate => matches.every(other => contains(candidate.words, other.words)));
    let remaining = brand ? words.replace(brand.words, ' ').trim() : words;
    // Product-family rules only narrow brand phrases ("xhorse key programmer"). Generic phrases
    // ("key programmer", "smart key shell") keep Shopify's native ranking, which is already relevant.
    let rule = brand ? rules.find(candidate => candidate.match.test(remaining)) : null;
    const accessory = /\b(?:adapters?|cables?|clips?|cases?|parts?|bits?|tracers?|jaws?|clamps?|blades?|replacement|covers?|belts?|brushes?|probes?)\b/.test(remaining);
    if (accessory) rule = null;
    if (brand && /\b(?:cutter|milling) bits?\b/.test(remaining)) {
      rule = { key: 'cutter_part', match: /\b(?:key cutting machines?|(?:cutter|milling) bits?)\b/g, titles: ['carbide cutter', 'milling cutter', 'cutting wheel'], exclude: ['programmer'] };
    }
    if (rule?.key === 'chip' && /\b(?:remotes?|fobs?|smart|flip)\b/.test(remaining)) rule = null;
    const accessBrand = brand && /\b(?:mircom|hid|farpointe|kaba|kantech|salto|rosslare|cdvi|indala)\b/.test(brand.words);
    if (!rule && accessBrand && /\b(?:credentials?|fobs?|keytags?|key tags?|proximity cards?)\b/.test(remaining) && !/\b(?:readers?|controllers?|systems?|panels?)\b/.test(remaining)) {
      rule = { key: 'credentials', match: /\b(?:(?:credit )?fobs?|(?:key )?fobs?|credentials?|keytags?|key tags?|proximity cards?)\b/g, titles: ['fob', 'keytag', 'key tag', 'proximity card', 'credential'], exclude: ['reader', 'controller', 'system', 'panel'] };
    }
    // A rule must never exclude a word the shopper typed ("lonsdor smart key shell").
    if (rule && rule.exclude.some(word => contains(remaining, normalize(word)))) rule = null;
    if (matches.length > 1 && !brand) return result;
    if (/\biclass\b/.test(remaining)) {
      result.kind = 'catalog';
      const generation = /\bse\b/.test(remaining) ? 'se' : /\blegacy\b/.test(remaining) ? 'legacy' : null;
      result.query = `${brand ? `vendor:${quote(brand.name)} AND ` : ''}title:iclass${generation ? ` AND title:${generation} -title:${generation === 'se' ? 'legacy' : 'se'}` : ''}`;
      remaining = remaining.replace(/\b(?:iclass|se|legacy)\b/g, ' ').trim();
      if (remaining) result.query += ` AND ${remaining.split(/\s+/).map(quote).join(' AND ')}`;
      if (!generation) result.refinements = ['HID iClass SE', 'HID iClass Legacy'];
      return result;
    }
    // Brand-only phrases keep native wording: vendor names are inconsistent in the catalog
    // (for example Lonsdor-BU30-01), so a vendor restriction would hide matching products.
    if (brand && !rule) return result;
    if (rule) {
      const clauses = brand ? [`vendor:${quote(brand.name)}`] : [];
      remaining = remaining.replace(rule.match, ' ');
      clauses.push(anyTitle(rule.titles));
      remaining = remaining.replace(/\b(?:i|need|want|buy|looking|look|search|please|a|an|the|some|for|to|purchase)\b/g, ' ').trim();
      const terms = remaining.split(/\s+/).filter(Boolean);
      if (terms.length) clauses.push(...terms.map(quote));
      result.query = `${clauses.join(' AND ')} ${exclusions(rule.exclude)}`;
      result.kind = 'catalog';
      result.brand = brand?.name;
    }
    if (/^(?:car )?key fobs?$|^fobs?$/.test(words)) result.refinements = ['car remote', 'RFID key fob'];
    if (/\biclass\b/.test(words) && !/\b(?:se|legacy)\b/.test(words)) result.refinements = ['HID iClass SE', 'HID iClass Legacy'];
    return result;
  }

  function urlFor(value, extras = {}) {
    const url = new URL(config.searchUrl || '/search', location.origin);
    url.search = new URLSearchParams({ q: value, type: 'product', 'options[prefix]': 'last', 'options[unavailable_products]': 'last', ...extras });
    return url;
  }

  function requestURL(value) {
    const url = new URL(value, location.origin);
    if (url.origin !== location.origin || url.pathname !== new URL(config.searchUrl || '/search', location.origin).pathname) return url;
    const intent = plan(url.searchParams.get('q'), url.searchParams.get('kr_raw') === '1');
    url.searchParams.set('q', intent.query);
    url.searchParams.set('type', 'product');
    return url;
  }

  function humanize(root, intent) {
    const escapedQuery = intent.query.replace(/[&<>"']/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character]));
    const queries = [...new Set([escapedQuery, intent.query])];
    const searchPath = new URL(config.searchUrl || '/search', location.origin).pathname;
    root.querySelectorAll('input[name="q"]').forEach(input => { input.value = intent.original; input.setAttribute('value', intent.original); });
    root.querySelectorAll('a[href]').forEach(link => {
      const url = new URL(link.getAttribute('href'), location.origin);
      if (url.origin !== location.origin || url.pathname !== searchPath) return;
      if (queries.includes(url.searchParams.get('q'))) {
        url.searchParams.set('q', intent.original);
        link.setAttribute('href', `${url.pathname}${url.search}${url.hash}`);
      }
    });
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    while (walker.nextNode()) {
      const node = walker.currentNode;
      if (node.parentElement?.closest('script, style, template')) continue;
      if (intent.query !== intent.original) {
        for (const query of queries) {
          if (node.textContent.includes(query)) node.textContent = node.textContent.replaceAll(query, intent.original);
        }
      }
    }
  }

  function guidance(intent, options = {}) {
    const node = document.createElement('div');
    node.className = 'keysrus-search-guidance';
    if (intent.corrected) {
      const message = document.createElement('p');
      message.textContent = (copy.showing_for || '').replace('[terms]', intent.label);
      const exact = document.createElement('a');
      exact.href = urlFor(intent.original, { kr_raw: '1' });
      exact.textContent = copy.search_original;
      const option = document.createElement('div');
      if (options.option) { option.id = 'predictive-search-option-original'; option.setAttribute('role', 'option'); option.setAttribute('aria-selected', 'false'); exact.tabIndex = -1; }
      option.append(exact);
      node.append(message, option);
    }
    if (options.escape && !intent.corrected && intent.kind !== 'native' && intent.query !== intent.original) {
      const option = document.createElement('p');
      const exact = document.createElement('a');
      exact.href = urlFor(intent.original, { kr_raw: '1' });
      exact.textContent = copy.search_original;
      option.append(exact);
      node.append(option);
    }
    if (intent.refinements.length) {
      const label = document.createElement('p');
      label.textContent = /iclass/i.test(intent.original) ? copy.iclass_tip : copy.choose_product_type;
      node.append(label);
      intent.refinements.forEach((term, index) => {
        const option = document.createElement(options.option ? 'div' : 'span');
        if (options.option) { option.id = `predictive-search-option-refine-${index}`; option.setAttribute('role', 'option'); option.setAttribute('aria-selected', 'false'); }
        const link = document.createElement('a'); link.href = urlFor(term); link.textContent = term;
        if (options.option) link.tabIndex = -1;
        option.append(link); node.append(option);
      });
    }
    return node;
  }

  window.KeysrusSearch = { plan, urlFor, requestURL, humanize, guidance, preferNative, copy };
})();
